import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Image as ImageIcon, ChevronRight, Camera, User, Filter } from "lucide-react";



import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const PhotosPage = () => {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");

  const categories = ["all", "Football", "Basketball", "Baseball", "Soccer", "Volleyball", "Track", "Tennis", "Swimming", "Wrestling", "Other"];

  useEffect(() => {
    fetchGalleries();
  }, []);

  const fetchGalleries = async () => {
    try {
      const res = await axios.get(`${API}/galleries?visible_only=true&limit=50`);
      setGalleries(res.data || []);
    } catch (err) {
      console.error("Error fetching galleries:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredGalleries = selectedCategory === "all" 
    ? galleries 
    : galleries.filter(g => g.category === selectedCategory);

  return (
    <main className="min-h-screen bg-[#050505]" data-testid="photos-page">
      
      

      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-zinc-900 to-[#050505] border-b border-white/5 pt-8 pb-12">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-center gap-3 mb-6">
            <Camera className="w-8 h-8 text-orange-500" />
            <h1 className="font-heading text-4xl md:text-5xl font-black uppercase tracking-tight text-white">
              Photos
            </h1>
          </div>
          <p className="text-zinc-400 text-lg max-w-2xl">
            Browse photos from games, events, and highlights from the Antler Sports Network.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-black border-b border-zinc-800 sticky top-[--header-height] z-30" style={{"--header-height": "60px"}}>
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-center gap-2 py-3 overflow-x-auto no-scrollbar">
            <Filter className="w-4 h-4 text-zinc-500 flex-shrink-0" />
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-1.5 text-sm font-bold uppercase whitespace-nowrap transition-colors ${
                  selectedCategory === cat
                    ? "bg-orange-500 text-black"
                    : "bg-zinc-900 text-zinc-400 hover:text-white"
                }`}
              >
                {cat === "all" ? "All Sports" : cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Galleries Grid */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-12">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
          </div>
        ) : filteredGalleries.length === 0 ? (
          <div className="text-center py-16">
            <ImageIcon className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-white mb-2">
              {selectedCategory === "all" ? "No Photos Yet" : `No ${selectedCategory} Photos`}
            </h2>
            <p className="text-zinc-500">Check back soon for photos and galleries.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredGalleries.map((gallery, index) => (
              <motion.div
                key={gallery.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={`/photos/${gallery.id}`}
                  className="group block relative aspect-[4/3] overflow-hidden bg-zinc-900"
                >
                  {gallery.cover_image_url ? (
                    <img
                      src={gallery.cover_image_url}
                      alt={gallery.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-16 h-16 text-zinc-700" />
                    </div>
                  )}
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent group-hover:via-black/50 transition-colors" />
                  
                  {/* Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    {gallery.category && (
                      <span className="text-orange-500 text-xs font-bold uppercase tracking-wider mb-2 block">
                        {gallery.category}
                      </span>
                    )}
                    <h3 className="text-white font-heading text-xl uppercase tracking-tight mb-2 group-hover:text-orange-500 transition-colors">
                      {gallery.title}
                    </h3>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-zinc-400 text-sm">
                          {gallery.images?.length || 0} {gallery.images?.length === 1 ? "photo" : "photos"}
                        </span>
                        
                        {/* Photographer Credit */}
                        {gallery.photographer && (
                          <span className="text-zinc-500 text-xs flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {gallery.photographer.name}
                          </span>
                        )}
                      </div>
                      
                      <span className="text-orange-500 flex items-center gap-1 text-sm font-bold uppercase opacity-0 group-hover:opacity-100 transition-opacity">
                        View <ChevronRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      
    </main>
  );
};

export default PhotosPage;
